import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';

interface Post {
  id: number;
  title: { rendered: string };
  slug: string;
  excerpt: { rendered: string };
  featured_media_url?: string;
  categories: number[];
  date: string;
  _embedded?: any;
}

interface AuthorPostsProps {
  authorSlug: string;
}

// Helper to decode HTML entities
function decodeHtml(html: string) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

// Helper to fetch attachment URL by ID
async function getAttachmentUrl(id: number | string) {
  try {
    const res = await fetch(`https://blog.thecryptotutor.com/wp-json/wp/v2/media/${id}`);
    const data = await res.json();
    return data.source_url || '/default-avatar.jpg';
  } catch {
    return '/default-avatar.jpg';
  }
}

export default function AuthorPosts({ authorSlug }: AuthorPostsProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [visiblePosts, setVisiblePosts] = useState(8);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authorAvatars, setAuthorAvatars] = useState<Record<number, string>>({});
  const [categoriesMap, setCategoriesMap] = useState<Record<number, string>>({});

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch author
        const authorRes = await fetch(
          `https://blog.thecryptotutor.com/wp-json/wp/v2/users?slug=${authorSlug}`
        );
        const authorData = await authorRes.json();
        if (!authorData[0]) throw new Error('Author not found');
        const authorId = authorData[0].id;

        // Fetch posts
        const postsRes = await fetch(
          `https://blog.thecryptotutor.com/wp-json/wp/v2/posts?author=${authorId}&per_page=100&_embed`
        );
        const postsData = await postsRes.json();
        setPosts(postsData);

        // Pre-fetch custom avatars
        postsData.forEach(async (post: Post) => {
          const author = post._embedded?.author?.[0];
          if (author?.acf?.custom_avatar && !authorAvatars[author.id]) {
            const url = await getAttachmentUrl(author.acf.custom_avatar);
            setAuthorAvatars((prev) => ({ ...prev, [author.id]: url }));
          }
        });

        // Build categories map from _embedded terms
        const catMap: Record<number, string> = {};
        postsData.forEach((post: Post) => {
          post._embedded?.['wp:term']?.forEach((termGroup: any) => {
            termGroup.forEach((term: any) => {
              if (term.taxonomy === 'category') catMap[term.id] = term.name;
            });
          });
        });
        setCategoriesMap(catMap);

      } catch (err: any) {
        setError(err.message || 'Error fetching posts');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [authorSlug]);

  const loadMore = () => setVisiblePosts((prev) => prev + 8);
  const displayedPosts = posts.slice(0, visiblePosts);

  if (loading) return <div className="text-center py-16">Loading posts...</div>;
  if (error) return <div className="text-center py-16 text-red-500">{error}</div>;
  if (!posts.length) return <div className="text-center py-16">No posts found for this author.</div>;

  return (
    <section className="py-10 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto">
        {/* White wrapper */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Articles by {authorSlug.replace('-', ' ')}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
            {displayedPosts.map((post) => {
              const featuredMedia =
                post._embedded?.['wp:featuredmedia']?.[0]?.source_url || '/default-image.jpg';
              const author = post._embedded?.author?.[0];
              const postCategories = post.categories
                .map((catId) => categoriesMap[catId])
                .filter(Boolean)
                .join(', ');

              const postDate = new Date(post.date).toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              });

              const authorAvatar =
                (author?.id && authorAvatars[author.id]) ||
                author?.avatar_urls?.['48'] ||
                '/default-avatar.jpg';

              const displayName = author
                ? author.acf?.full_name || author.name
                : 'Author';

              return (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group h-full">
                  <Card hover className="flex flex-col h-full overflow-hidden">
                    {/* Featured Image */}
                    <div className="aspect-video overflow-hidden flex-shrink-0">
                      <img
                        src={featuredMedia}
                        alt={decodeHtml(post.title.rendered)}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>

                    <div className="p-4 flex flex-col flex-1">
                      {/* Title */}
                      <h3
                        className="font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors"
                        dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                      />

                      {/* Author + Date */}
                      <div className="flex items-center justify-between text-xs text-gray-500 space-x-2">
                        <div className="flex items-center space-x-2">
                          <img
                            src={authorAvatar}
                            alt={displayName}
                            className="w-5 h-5 rounded-lg object-cover"
                          />
                          {author && (
                            <Link
                              to={`/author/${author.slug}`}
                              className="hover:text-blue-600 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {displayName}
                            </Link>
                          )}
                        </div>
                        <span>{postDate}</span>
                      </div>

                      {/* Categories */}
                      {postCategories && (
                        <div className="mt-2 text-xs text-blue-600 font-medium truncate">
                          {postCategories}
                        </div>
                      )}
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Load More Button */}
          {visiblePosts < posts.length && (
            <div className="flex justify-center">
              <button
                onClick={loadMore}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-[20px] transition-colors duration-300 cursor-pointer"
              >
                Load More Articles
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
